/**
 * EmailInput Component
 * Reusable email input with validation for auth and guest forms
 */

import { cn } from '@/lib/utils';
import { SPACING } from '@/design-system';
import { Label } from '@/components/ui/label';
import { ValidatedInput } from '@/components/FormValidation';
import { LCommon } from '@/components/i18n';
import { useLocalizations } from '@/hooks/useLocalizations';
import { createEmailSchema } from './schemas';

interface EmailInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  placeholder?: string;
  compact?: boolean;
  hideLabel?: boolean;
}

export function EmailInput({
  id = 'email',
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  required = true,
  label,
  placeholder,
  compact = false,
  hideLabel = false,
}: EmailInputProps) {
  const { lCommon, lComponents } = useLocalizations();

  const emailSchema = createEmailSchema({
    required: lCommon('validation.email_required', { defaultValue: 'Email is required' }),
    invalid: lCommon('validation.invalid_email', { defaultValue: 'Invalid email address' }),
  });

  const handleBlur = () => {
    onBlur?.();
  };

  return (
    <div className={compact ? 'space-y-1' : SPACING.formFieldGap}>
      {!hideLabel && (
        <Label htmlFor={id} className={compact ? 'text-sm' : undefined}>
          {label || <LCommon id="labels.email" defaultValue="Email" />}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </Label>
      )}
      <ValidatedInput
        id={id}
        type="email"
        placeholder={placeholder || lComponents('compose_message.guest.email_placeholder', { defaultValue: 'Enter your email' })}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={handleBlur}
        disabled={disabled}
        error={error}
        required={required}
        className={compact ? 'h-9' : undefined}
      />
    </div>
  );
}
